import React, { useState, useEffect } from 'react';

export const ObservationConfigDrawer = ({
  isOpen,
  onClose,
  mapping,
  investigationName,
  onSaveConfig,
}) => {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    if (mapping) {
      setConfig({
        ...mapping,
        prefix: mapping.prefix || '',
        childFlag: !!mapping.childFlag,
        isBold: !!mapping.isBold,
        isUnderLine: !!mapping.isUnderLine,
        sepratePrint: !!mapping.sepratePrint,
        showNabl: mapping.showNabl !== undefined ? !!mapping.showNabl : true,
        isComment: !!mapping.isComment,
        chkHelp: !!mapping.chkHelp,
        microscopy: !!mapping.microscopy,
        isCritical: !!mapping.isCritical,
        isAmr: !!mapping.isAmr,
        isReflex: !!mapping.isReflex,
        isAutoConsume: !!mapping.isAutoConsume,
      });
    }
  }, [mapping]);

  if (!isOpen || !config) return null;

  const handleToggle = (field) => {
    setConfig(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleTextChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    onSaveConfig(config);
    onClose();
  };

  return (
    <div className="iom-drawer-overlay" onClick={onClose}>
      <div className="iom-drawer-panel iom-drawer-panel-config" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="iom-drawer-header">
          <div>
            <div className="iom-drawer-kicker">MAPPING SPECIFICATIONS</div>
            <h3 className="iom-drawer-title">{config.observationName || config.labObservationId}</h3>
            <div className="iom-config-meta-chips">
              <span className="iom-chip-code">{config.labObservationId}</span>
              <span className="iom-chip-format">{config.readingFormat || 'Numeric'}</span>
              {config.unit && <span className="iom-chip-unit">{config.unit}</span>}
              <span className="iom-chip-parent">Mapped in: {investigationName || 'Current Test'}</span>
            </div>
          </div>
          <button type="button" className="iom-btn-close" onClick={onClose} title="Close drawer">
            ✕
          </button>
        </div>

        {/* Configuration Body */}
        <form onSubmit={handleSave} className="iom-drawer-form-body">
          {/* Group 1: Display & Report Typography */}
          <div className="iom-cfg-group">
            <div className="iom-cfg-group-header">
              <span className="iom-cfg-group-icon">🎨</span>
              <div>
                <h4 className="iom-cfg-group-title">Display & Report Typography</h4>
                <p className="iom-cfg-group-desc">Configure visual styling, indentation, and page layout on patient reports</p>
              </div>
            </div>

            <div className="iom-cfg-fields">
              <div className="iom-form-group mb-3">
                <label className="iom-label">
                  Visual Prefix / Indentation
                  <span className="iom-label-hint">Added before the parameter name in reports (e.g. "-- " or "• ")</span>
                </label>
                <input
                  type="text"
                  className="iom-input"
                  placeholder="e.g.   --  or Sub-Group"
                  value={config.prefix}
                  onChange={e => handleTextChange('prefix', e.target.value)}
                />
              </div>

              {/* Setting Toggle: Header Row */}
              <div className="iom-toggle-row">
                <div className="iom-toggle-info">
                  <div className="iom-toggle-label">Category / Section Header Row</div>
                  <div className="iom-toggle-sub">
                    Renders this item as a group header divider. Does not accept direct patient test results.
                  </div>
                </div>
                <label className="iom-switch">
                  <input
                    type="checkbox"
                    checked={config.childFlag}
                    onChange={() => handleToggle('childFlag')}
                  />
                  <span className="iom-slider" />
                </label>
              </div>

              {/* Setting Toggle: Bold Typography */}
              <div className="iom-toggle-row">
                <div className="iom-toggle-info">
                  <div className="iom-toggle-label">Bold Typography</div>
                  <div className="iom-toggle-sub">
                    Prints the parameter title and numeric result in bold font weight for visual emphasis.
                  </div>
                </div>
                <label className="iom-switch">
                  <input
                    type="checkbox"
                    checked={config.isBold}
                    onChange={() => handleToggle('isBold')}
                  />
                  <span className="iom-slider" />
                </label>
              </div>

              {/* Setting Toggle: Underline Typography */}
              <div className="iom-toggle-row">
                <div className="iom-toggle-info">
                  <div className="iom-toggle-label">Underline Typography</div>
                  <div className="iom-toggle-sub">
                    Applies underline styling beneath the parameter header or label on printed formats.
                  </div>
                </div>
                <label className="iom-switch">
                  <input
                    type="checkbox"
                    checked={config.isUnderLine}
                    onChange={() => handleToggle('isUnderLine')}
                  />
                  <span className="iom-slider" />
                </label>
              </div>

              {/* Setting Toggle: Print Separate */}
              <div className="iom-toggle-row">
                <div className="iom-toggle-info">
                  <div className="iom-toggle-label">Print on Separate Section / Page</div>
                  <div className="iom-toggle-sub">
                    Forces this observation and its sub-findings into an isolated report section or dedicated page.
                  </div>
                </div>
                <label className="iom-switch">
                  <input
                    type="checkbox"
                    checked={config.sepratePrint}
                    onChange={() => handleToggle('sepratePrint')}
                  />
                  <span className="iom-slider" />
                </label>
              </div>
            </div>
          </div>

          {/* Group 2: Compliance & Narrative Reporting */}
          <div className="iom-cfg-group">
            <div className="iom-cfg-group-header">
              <span className="iom-cfg-group-icon">📜</span>
              <div>
                <h4 className="iom-cfg-group-title">Reporting & Quality Compliance</h4>
                <p className="iom-cfg-group-desc">Accreditation badges, interpretive comments, and constrained input validation</p>
              </div>
            </div>

            <div className="iom-cfg-fields">
              {/* Setting Toggle: Show NABL */}
              <div className="iom-toggle-row">
                <div className="iom-toggle-info">
                  <div className="iom-toggle-label">Show NABL Accreditation Mark</div>
                  <div className="iom-toggle-sub">
                    Displays ISO 15189 / NABL certified accreditation mark next to this observation.
                  </div>
                </div>
                <label className="iom-switch">
                  <input
                    type="checkbox"
                    checked={config.showNabl}
                    onChange={() => handleToggle('showNabl')}
                  />
                  <span className="iom-slider" />
                </label>
              </div>

              {/* Setting Toggle: Comment Only */}
              <div className="iom-toggle-row">
                <div className="iom-toggle-info">
                  <div className="iom-toggle-label">Comment / Narrative Parameter</div>
                  <div className="iom-toggle-sub">
                    Formats this parameter as an expansive doctor commentary or clinical impression section.
                  </div>
                </div>
                <label className="iom-switch">
                  <input
                    type="checkbox"
                    checked={config.isComment}
                    onChange={() => handleToggle('isComment')}
                  />
                  <span className="iom-slider" />
                </label>
              </div>

              {/* Setting Toggle: Help Value Only */}
              <div className="iom-toggle-row">
                <div className="iom-toggle-info">
                  <div className="iom-toggle-label">Help Value Only (Restricted Canned Pick-list)</div>
                  <div className="iom-toggle-sub">
                    Restricts lab technician from entering arbitrary text; results must be picked from the configured help list.
                  </div>
                </div>
                <label className="iom-switch">
                  <input
                    type="checkbox"
                    checked={config.chkHelp}
                    onChange={() => handleToggle('chkHelp')}
                  />
                  <span className="iom-slider" />
                </label>
              </div>

              {/* Setting Toggle: Microscopy */}
              <div className="iom-toggle-row">
                <div className="iom-toggle-info">
                  <div className="iom-toggle-label">Microscopy Slide Examination</div>
                  <div className="iom-toggle-sub">
                    Enables manual morphology slide examination workflow and slide count tracking.
                  </div>
                </div>
                <label className="iom-switch">
                  <input
                    type="checkbox"
                    checked={config.microscopy}
                    onChange={() => handleToggle('microscopy')}
                  />
                  <span className="iom-slider" />
                </label>
              </div>
            </div>
          </div>

          {/* Group 3: Clinical Triggers & Automated Processing */}
          <div className="iom-cfg-group">
            <div className="iom-cfg-group-header">
              <span className="iom-cfg-group-icon">⚡</span>
              <div>
                <h4 className="iom-cfg-group-title">Clinical Triggers & Laboratory Automation</h4>
                <p className="iom-cfg-group-desc">Automated panic alerting, AMR limits, reflex rule cascades, and reagent tracking</p>
              </div>
            </div>

            <div className="iom-cfg-fields">
              {/* Setting Toggle: Critical / Panic */}
              <div className="iom-toggle-row">
                <div className="iom-toggle-info">
                  <div className="iom-toggle-label">Critical / Panic Value Alerting</div>
                  <div className="iom-toggle-sub">
                    Triggers urgent clinical alert popups, SMS/email dispatches, and audit logs when panic limits are breached.
                  </div>
                </div>
                <label className="iom-switch">
                  <input
                    type="checkbox"
                    checked={config.isCritical}
                    onChange={() => handleToggle('isCritical')}
                  />
                  <span className="iom-slider" />
                </label>
              </div>

              {/* Setting Toggle: AMR */}
              <div className="iom-toggle-row">
                <div className="iom-toggle-info">
                  <div className="iom-toggle-label">Analytical Measurement Range (AMR) Tracking</div>
                  <div className="iom-toggle-sub">
                    Enforces analyzer reportable measurement range rules and sample dilution warnings.
                  </div>
                </div>
                <label className="iom-switch">
                  <input
                    type="checkbox"
                    checked={config.isAmr}
                    onChange={() => handleToggle('isAmr')}
                  />
                  <span className="iom-slider" />
                </label>
              </div>

              {/* Setting Toggle: Reflex */}
              <div className="iom-toggle-row">
                <div className="iom-toggle-info">
                  <div className="iom-toggle-label">Reflex Test Trigger</div>
                  <div className="iom-toggle-sub">
                    Enables rule-based cascade testing (e.g. abnormal TSH automatically queues Free T3/T4 testing).
                  </div>
                </div>
                <label className="iom-switch">
                  <input
                    type="checkbox"
                    checked={config.isReflex}
                    onChange={() => handleToggle('isReflex')}
                  />
                  <span className="iom-slider" />
                </label>
              </div>

              {/* Setting Toggle: Auto Consume */}
              <div className="iom-toggle-row">
                <div className="iom-toggle-info">
                  <div className="iom-toggle-label">Automated Reagent & Inventory Consumption</div>
                  <div className="iom-toggle-sub">
                    Automatically deducts linked reagent lot stock from inventory when results for this observation are verified.
                  </div>
                </div>
                <label className="iom-switch">
                  <input
                    type="checkbox"
                    checked={config.isAutoConsume}
                    onChange={() => handleToggle('isAutoConsume')}
                  />
                  <span className="iom-slider" />
                </label>
              </div>
            </div>
          </div>

          {/* Drawer Footer Actions */}
          <div className="iom-drawer-footer">
            <div className="iom-footer-summary">
              <span className="text-muted">Configuration will update this parameter mapping</span>
            </div>
            <div className="iom-footer-buttons">
              <button
                type="button"
                className="iom-btn iom-btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="iom-btn iom-btn-primary"
              >
                Apply Configuration
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
